/**
 * Postcode + polygon utilities used by the Prospect screen.
 *
 * Postcode lookup uses postcodes.io (free, public). For production, cache
 * results in localStorage to keep latency down and respect their fair-use.
 */

const POSTCODES_IO_BASE = 'https://api.postcodes.io';

export interface PostcodeLookup {
  postcode: string;
  longitude: number;
  latitude: number;
  admin_district?: string;
  admin_ward?: string;
}

const cache = new Map<string, PostcodeLookup>();

export async function lookupPostcode(postcode: string): Promise<PostcodeLookup | null> {
  const normalised = postcode.trim().toUpperCase().replace(/\s+/g, ' ');
  if (cache.has(normalised)) return cache.get(normalised)!;
  try {
    const r = await fetch(`${POSTCODES_IO_BASE}/postcodes/${encodeURIComponent(normalised)}`);
    if (!r.ok) return null;
    const j = await r.json();
    if (j.status !== 200 || !j.result) return null;
    const out: PostcodeLookup = {
      postcode: j.result.postcode,
      longitude: j.result.longitude,
      latitude: j.result.latitude,
      admin_district: j.result.admin_district,
      admin_ward: j.result.admin_ward,
    };
    cache.set(normalised, out);
    return out;
  } catch (err) {
    console.warn('postcode lookup failed', err);
    return null;
  }
}

export async function lookupPostcodesBulk(postcodes: string[]): Promise<PostcodeLookup[]> {
  if (!postcodes.length) return [];
  // postcodes.io supports up to 100 per call.
  const out: PostcodeLookup[] = [];
  for (let i = 0; i < postcodes.length; i += 100) {
    const batch = postcodes.slice(i, i + 100);
    try {
      const r = await fetch(`${POSTCODES_IO_BASE}/postcodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: batch }),
      });
      if (!r.ok) continue;
      const j = await r.json();
      if (Array.isArray(j.result)) {
        for (const item of j.result) {
          if (item.result) {
            out.push({
              postcode: item.result.postcode,
              longitude: item.result.longitude,
              latitude: item.result.latitude,
              admin_district: item.result.admin_district,
            });
          }
        }
      }
    } catch (err) {
      console.warn('bulk postcode lookup failed', err);
    }
  }
  return out;
}

/**
 * Convert a list of points into a square-ish bounding polygon (GeoJSON Polygon).
 * Useful for a quick "all postcodes I selected" fallback when the user hasn't
 * drawn a polygon themselves.
 */
export function pointsToBoundingPolygon(
  points: Array<{ longitude: number; latitude: number }>,
  paddingDeg = 0.005
): GeoJSON.Polygon | null {
  if (!points.length) return null;
  const lngs = points.map((p) => p.longitude);
  const lats = points.map((p) => p.latitude);
  const minLng = Math.min(...lngs) - paddingDeg;
  const maxLng = Math.max(...lngs) + paddingDeg;
  const minLat = Math.min(...lats) - paddingDeg;
  const maxLat = Math.max(...lats) + paddingDeg;
  return {
    type: 'Polygon',
    coordinates: [[
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ]],
  };
}

/**
 * Approximate area in km² of a GeoJSON polygon (rough — flat-earth heuristic
 * fine for UK-scale territory selection).
 */
export function approxPolygonAreaKm2(polygon: GeoJSON.Polygon): number {
  if (!polygon || !polygon.coordinates || !polygon.coordinates[0]) return 0;
  const ring = polygon.coordinates[0];
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    area += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  area = Math.abs(area / 2);
  // 1 deg lng × 1 deg lat at UK lat ~ 70 × 111 km
  return area * 70 * 111;
}
